import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Tab,
  Tabs,
  Snackbar,
  Alert,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CreateIcon from "@mui/icons-material/Create";
import TechnicalIssueForm from "../components/TechnicalIssueForm";
import TechnicalIssueList from "../components/TechnicalIssueList";
import { getReports } from "../services/technicalIssuesStorageService";

function StatCard({ label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 3,
        py: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        flex: 1,
        minWidth: 140,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ color: color || "text.primary" }}
      >
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Paper>
  );
}

export default function TechnicalIssuesPage() {
  const [tab, setTab] = useState(0);
  const [reports, setReports] = useState(() => getReports());
  const [snackbar, setSnackbar] = useState(false);

  const refresh = useCallback(() => {
    setReports(getReports());
  }, []);

  const handleFormSuccess = () => {
    refresh();
    setSnackbar(true);
    setTab(1);
  };

  const totalReports = reports.length;
  const openCount = reports.filter((r) => r.status === "open").length;
  const inProgressCount = reports.filter((r) => r.status === "in_progress").length;
  const resolvedCount = reports.filter((r) => r.status === "resolved").length;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #EF4444 0%, #F97316 100%)",
          }}
        >
          <BugReportIcon sx={{ color: "white", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Technical Issues
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Report and track issues found in the application
          </Typography>
        </Box>
      </Stack>

      {/* Stats Row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <StatCard label="Total" value={totalReports} color="#374151" />
        <StatCard label="Open" value={openCount} color="#DC2626" />
        <StatCard label="In Progress" value={inProgressCount} color="#2563EB" />
        <StatCard label="Resolved" value={resolvedCount} color="#059669" />
      </Stack>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            pt: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab icon={<CreateIcon />} iconPosition="start" label="Report Issue" />
          <Tab icon={<ListAltIcon />} iconPosition="start" label="My Issues" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && <TechnicalIssueForm onSuccess={handleFormSuccess} />}
          {tab === 1 && <TechnicalIssueList reports={reports} onRefresh={refresh} />}
        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          Technical issue submitted successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}
